import {
  handleDisableVocabularySupport,
  handleHideLanguageSelector,
  handleHideQuizReveal,
  handleHideReadAloud,
  handleHideTranslation,
} from "@/assignments/utils/assignmentActions";
import ArticleLevel from "@/types/models/ArticleLevel.interface";
import {
  Assignment,
  AssignmentConfig,
} from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import LanguageSetting from "@/view/AdditionalAssignmentSettings/AssignmentBuilderAdditionalSettings/LanguageSetting";
import QuizAnswersSetting from "@/view/AdditionalAssignmentSettings/AssignmentBuilderAdditionalSettings/QuizAnswersSetting";
import ReadAloudSetting from "@/view/AdditionalAssignmentSettings/AssignmentBuilderAdditionalSettings/ReadAloudSetting";
import TranslationSetting from "@/view/AdditionalAssignmentSettings/AssignmentBuilderAdditionalSettings/TranslationSetting";
import VocabularySupportSetting from "@/view/AdditionalAssignmentSettings/AssignmentBuilderAdditionalSettings/VocabularySupportSetting";
import ScheduledInput from "@/view/ScheduledInput/ScheduledInput";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";
import useTranslatorVisibility from "@/assignments/useTranslatorVisibility/useTranslatorVisibility";
import { ContentType } from "@/view/constants/contentTypes";
import {
  checkboxSectionCss,
  dateInputCss,
  sectionCss,
  titleCss,
} from "./AdditionalAssignmentSettings.style";
import { CONFIG_KEYS } from "./AdditionalAssignmentSettings.constants";

const AdditionalAssignmentSettings = ({
  assignment,
  content,
  currentLevel,
  dispatch,
  toggleVocabularySupport,
  resetDate,
  userProperties,
}: {
  assignment: Assignment;
  content: {
    content_type: string;
    data: {
      article_levels: ArticleLevel[];
    };
    id: string;
    is_lite: boolean;
  };
  currentLevel: ArticleLevel;
  dispatch: () => void;
  toggleVocabularySupport: (arg: boolean) => void;
  resetDate: string | null;
  userProperties: UserProperties;
}) => {
  const { isTranslatorAssignmentConfigVisible } = useTranslatorVisibility({
    content: {
      content_type: content?.content_type as ContentType,
      data: content?.data,
    },
    language: currentLevel.language,
  });

  const assignmentId = assignment?.content_id;

  const isReadAloudHidden = getAssignmentSetting(
    CONFIG_KEYS.hide_read_aloud as keyof AssignmentConfig,
    assignment,
  );

  const isQuizRevealHidden = getAssignmentSetting(
    CONFIG_KEYS.hide_quiz_answers as keyof AssignmentConfig,
    assignment,
  );

  const isHideLanguageChecked = getAssignmentSetting(
    CONFIG_KEYS.hide_language_selector as keyof AssignmentConfig,
    assignment,
  );

  const isVocabularySupportDisabled = getAssignmentSetting(
    CONFIG_KEYS.disable_vocabulary_support as keyof AssignmentConfig,
    assignment,
  );

  const isTranslationHidden = getAssignmentSetting(
    CONFIG_KEYS.hide_translation as keyof AssignmentConfig,
    assignment,
  );

  const onHideQuizRevealChange = (isQuizRevealHidden: boolean) => {
    handleHideQuizReveal({ hide_quiz_answers: isQuizRevealHidden }, dispatch);
  };

  const onHideReadAloudChange = (isReadAloudHidden: boolean) => {
    handleHideReadAloud({ hide_read_aloud: isReadAloudHidden }, dispatch);
  };

  const onHideLanguageSelectorChange = (isHideLanguageChecked: boolean) => {
    handleHideLanguageSelector(
      { hide_language_selector: isHideLanguageChecked },
      dispatch,
    );
  };

  const onDisableVocabularySupport = (isVocabularySupportDisabled: boolean) => {
    handleDisableVocabularySupport(
      { disable_vocabulary_support: isVocabularySupportDisabled },
      dispatch,
    );
    toggleVocabularySupport(!isVocabularySupportDisabled);
  };

  const onHideTranslation = (isTranslationHidden: boolean) => {
    handleHideTranslation({ hide_translation: isTranslationHidden }, dispatch);
  };

  return (
    <div css={sectionCss}>
      <h3 css={titleCss}>Additional Settings</h3>

      <div css={dateInputCss}>
        <ScheduledInput
          assignment={assignment}
          content={content}
          userProperties={userProperties}
          dispatch={dispatch}
          resetDate={resetDate}
        />
      </div>

      <div css={checkboxSectionCss}>
        <ReadAloudSetting
          assignmentId={assignmentId}
          currentContentLevel={currentLevel}
          isReadAloudHidden={isReadAloudHidden}
          onHideReadAloudChange={onHideReadAloudChange}
        />
        <LanguageSetting
          assignmentId={assignmentId}
          currentContentLevel={currentLevel}
          isLanguageSelectorHidden={isHideLanguageChecked}
          onHideLanguageSelectorChange={onHideLanguageSelectorChange}
        />
        <QuizAnswersSetting
          assignmentId={assignmentId}
          currentContentLevel={currentLevel}
          isQuizRevealHidden={isQuizRevealHidden}
          onHideQuizRevealChange={onHideQuizRevealChange}
        />
        <VocabularySupportSetting
          assignmentId={assignmentId}
          currentContentLevel={currentLevel}
          disableVocabularySupport={isVocabularySupportDisabled}
          onDisableVocabularySupport={onDisableVocabularySupport}
        />
        {isTranslatorAssignmentConfigVisible ? (
          <TranslationSetting
            assignmentId={assignmentId}
            currentContentLevel={currentLevel}
            isTranslationHidden={isTranslationHidden}
            onHideTranslation={onHideTranslation}
          />
        ) : null}
      </div>
    </div>
  );
};

export default AdditionalAssignmentSettings;
